import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStoreActions } from 'easy-peasy';
import { toast } from 'react-toastify';

// Import Components
import Table from '../Table';

// Import icons
import Search from '../../../assets/images/svg/search.svg';

// format date
import { dateFormat } from '../../tools/manager';

const AllCouponsOrders = () => {
  const navigation = useNavigate();

  //  🌱 Store actions
  const getListOrders = useStoreActions(
    (actions) => actions.middleware.OrdersMiddleware.getListOrders
  );
  const getUser = useStoreActions(
    (actions) => actions.middleware.AdminMiddleware.getAdmin
  );

  // 🌱 États (données + pagination + recherche)
  const [orderCoupons, setOrderCoupons] = useState([]);        // données cumulées (toutes les pages)
  const [listOrderCoupons, setListOrderCoupons] = useState([]); // données filtrées (search)
  const [textSearch, setTextSearch] = useState('');

  const [nextToken, setNextToken] = useState(null);     // pagination token
  const [isLoadingMore, setIsLoadingMore] = useState(false); // état bouton "Charger plus"

  // 🔍 Filtre de recherche
  useEffect(() => {
    if (textSearch === '') {
      setListOrderCoupons(orderCoupons);
    } else {
      const lower = textSearch.toLowerCase();
      const filtered = orderCoupons.filter((element) => {
        const companyName = element?.worksite?.company?.name?.toLowerCase() || '';
        const worksiteName = element?.worksite?.name?.toLowerCase() || '';
        const worksiteType = element?.worksite?.type?.name?.toLowerCase() || '';
        const numero = element?.numero || '';
        const firstName = element?.collaborator?.first_name?.toLowerCase() || '';
        const lastName = element?.collaborator?.last_name?.toLowerCase() || '';
        const date = dateFormat(element?.createdAt);

        return (
          companyName.includes(lower) ||
          worksiteName.includes(lower) ||
          worksiteType.includes(lower) ||
          numero.includes(textSearch) ||
          firstName.includes(lower) ||
          lastName.includes(lower) ||
          date.day.includes(textSearch) ||
          date.month.includes(textSearch) ||
          date.year.includes(textSearch)
        );
      });

      setListOrderCoupons(filtered);
    }
  }, [orderCoupons, textSearch]);

  // 🌱 Charge une page de bons de commande (initiale ou "Charger plus")
  const fetchOrderCoupons = async (token = null) => {
    try {
      if (token) {
        setIsLoadingMore(true);
      }

      // 🔹 Appel avec pagination (limit + nextToken)
      const res = await getListOrders({
        limit: 25,
        nextToken: token,
      });

      // 🔹 Compat: si jamais le thunk renvoie encore un simple tableau
      const rawItems = Array.isArray(res) ? res : res?.items || [];
      const newToken = Array.isArray(res) ? null : res?.nextToken || null;

      // 🔹 Enrichissement collaborator si manquant
      const enriched = await Promise.all(
        rawItems.map(async (el) => {
          if (!el?.collaborator && el?.orderCouponCollaboratorId) {
            try {
              const user = await getUser(el.orderCouponCollaboratorId);
              return {
                ...el,
                collaborator: {
                  first_name: user?.first_name || '',
                  last_name: user?.last_name || '',
                },
              };
            } catch {
              // en cas d'erreur, on laisse tel quel
              return el;
            }
          }
          return el;
        })
      );

      // 🔹 Merge avec l'existant si on charge plus
      setOrderCoupons((prev) =>
        token ? [...prev, ...enriched] : enriched
      );

      // 🔹 Sauvegarde du nouveau nextToken pour la suite
      setNextToken(newToken);
    } catch (error) {
      console.error(error);
      toast.error(
        '❗️ Une erreur est survenue lors du chargement de la liste des bons de commande'
      );
    } finally {
      setIsLoadingMore(false);
    }
  };

  // 🌱 Premier chargement
  useEffect(() => {
    fetchOrderCoupons(null);
  }, []);

  // 🔁 Handler "Charger plus"
  const handleLoadMore = () => {
    if (nextToken && !isLoadingMore) {
      fetchOrderCoupons(nextToken);
    }
  };

  return (
    <section className="md:pt-8 px-4 md:px-9">
      {/* div search bar */}
      <div className="relative flex mt-6 border border-gray-admin rounded-xl">
        <input
          type="text"
          placeholder="Recherche bons"
          className="w-full py-3 pl-10 border-none rounded-xl placeholder:text-blue-admin inputFocus"
          onInput={(e) => setTextSearch(e.target.value)}
        />

        <img
          src={Search}
          alt="search icon"
          className="absolute left-0 flex items-center w-8 h-5 pl-3 top-4"
        />
      </div>

      {/* Table liste orders coupon */}
      <div className="mt-9">
        <Table>
          <thead>
            <tr>
              <th>Réalisé le :</th>
              <th>Fait par</th>
              <th>Numéro </th>
              <th>Type de chantier</th>
              <th>Nom du client</th>
              <th className="flex justify-end">Nom du chantier</th>
            </tr>
          </thead>
          <tbody>
            {listOrderCoupons?.length ? (
              listOrderCoupons
                // Si ton backend trie déjà par date DESC via l'index, tu peux enlever ce sort si tu veux
                ?.sort((a, b) => b.numero - a.numero)
                ?.map((orderCoupon) => {
                  const dateCouponCreate = dateFormat(orderCoupon.createdAt);
                  return (
                    <tr
                      key={orderCoupon.id}
                      className={`h-16 border-b border-white-admin hover:bg-blue-50 transition-all ${orderCoupon.ctm !== 'true' ? 'cursor-pointer' : ''
                        }`}
                      onClick={() => {
                        if (orderCoupon.ctm !== 'true') {
                          navigation(`/pdf-preview/order/${orderCoupon.id}`);
                        }
                      }}
                    >
                      <td>
                        {dateCouponCreate.day}.{dateCouponCreate.month}.
                        {dateCouponCreate.year}
                      </td>
                      <td>
                        {orderCoupon?.collaborator?.first_name}{' '}
                        {orderCoupon?.collaborator?.last_name}
                      </td>
                      <td className="text-left">
                        {orderCoupon?.numero}{' '}
                        {orderCoupon.ctm !== 'true' ? '' : '- pré-rempli'}
                      </td>
                      <td>{orderCoupon?.worksite?.type?.name}</td>
                      <td>{orderCoupon?.worksite?.company?.name}</td>
                      <td className="flex justify-end mt-5">
                        {orderCoupon?.worksite?.name}
                      </td>
                    </tr>
                  );
                })
            ) : (
              <tr>
                <td
                  className="text-center text-lg font-semibold pt-20"
                  colSpan={6}
                >
                  {textSearch
                    ? "Oups, ce chantier ou client n'existe pas 🤭"
                    : 'Ajouter un bon de commande'}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Bouton "Charger plus" si pagination disponible */}
      {nextToken && (
        <div className="flex justify-center mt-6 mb-8">
          <button
            type="button"
            onClick={handleLoadMore}
            disabled={isLoadingMore}
            className="px-5 py-2 border border-blue-admin rounded-xl text-blue-admin hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            {isLoadingMore ? 'Chargement...' : 'Charger plus'}
          </button>
        </div>
      )}
    </section>
  );
};

export default AllCouponsOrders;
